import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PermissionsService } from '../services/permissions.service';

/**
 * HTTP Interceptor for adding authentication token to requests
 * and handling authentication errors
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const permissionsService = inject(PermissionsService);

    // Get token from storage
    const token = getToken();

    // Skip token for public endpoints
    const isPublicEndpoint = req.url.includes('/admin/login') ||
        req.url.includes('/user/login') ||
        req.url.includes('/user/register');

    // Clone request and add authorization header if token exists
    let authReq = req;
    if (token && !isPublicEndpoint) {
        authReq = req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    // Handle the request and catch authentication errors
    return next(authReq).pipe(
        catchError((error) => {
            // Handle 401 Unauthorized - clear session and redirect to login
            if (error.status === 401) {
                console.error('Authentication failed (401), clearing session and redirecting to login...');

                // Clear token from storage
                clearToken();

                // Clear current user from AuthService
                authService.setCurrentUser(null);

                // Clear permissions cache
                permissionsService.clearCache();

                // Redirect to admin login
                router.navigate(['/admin/login']);
            }

            // Handle 403 Forbidden - Permission denied
            if (error.status === 403) {
                console.error('Access forbidden - insufficient permissions');
                console.error('Error message:', error.error?.message || 'No tienes permiso para acceder a este recurso');
                // Note: The component/service should handle displaying the error message to the user
                // We don't redirect on 403, just log and pass the error through
            }

            return throwError(() => error);
        })
    );
};

/**
 * Helper function to get token from storage
 */
function getToken(): string | null {
    return localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
}

/**
 * Helper function to clear token from storage
 */
function clearToken(): void {
    localStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_token');
}
